"use client";

import { useState, useEffect, useCallback } from "react";
import {
  libraryApi,
  type LibraryArticle,
  type LibraryApiResponse,
} from "@/api";

interface UseLibraryPageDataReturn {
  articles: LibraryArticle[];
  loading: boolean;
  error: string | null;
  meta: LibraryApiResponse["meta"] | null;
  refreshData: () => void;
  loadMore: () => void;
  loadByCategory: (category: string) => void;
}

export function useLibraryPageData(): UseLibraryPageDataReturn {
  const [articles, setArticles] = useState<LibraryArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<LibraryApiResponse["meta"] | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchLibraryData = useCallback(async (page = 1, append = false) => {
    try {
      setLoading(true);
      setError(null);

      const response = await libraryApi.list({
        page,
        limit: 6,
        sort: "-createdAt", // Sort by creation date, newest first
      });

      if (!response.success) {
        throw new Error(
          response.error?.message || "Failed to fetch library data"
        );
      }

      const result: any = response.data!;
      if (append && page > 1) {
        setArticles((prev) => [...prev, ...result]);
      } else {
        setArticles(result);
      }
      setMeta(result.meta);
      setCurrentPage(page);
    } catch (err) {
      console.error("Error fetching library data:", err);
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );

      // Set empty state on error
      if (!append) {
        setArticles([]);
        setMeta(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchLibraryData(1, false);
  }, [fetchLibraryData]);

  // Refresh data function
  const refreshData = useCallback(() => {
    fetchLibraryData(1, false);
  }, [fetchLibraryData]);

  // Load more function for pagination
  const loadMore = useCallback(() => {
    if (meta && meta.page < meta.pageCount) {
      fetchLibraryData(currentPage + 1, true);
    }
  }, [fetchLibraryData, currentPage, meta]);

  // Load by category function (kept for compatibility but does nothing)
  const loadByCategory = useCallback((category: string) => {
    // No-op since we removed category functionality
  }, []);

  return {
    articles,
    loading,
    error,
    meta,
    refreshData,
    loadMore,
    loadByCategory,
  };
}
